import React, { useState, useEffect, useCallback } from "react";

import {
  listApiProfiles,
  createApiProfile,
  updateApiProfile,
  deleteApiProfile,
  listProfileTags,
  fetchTagsByIds,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import { convertToMs, msToHuman, headersToJson } from "../../../../utils/timeUtils";
import Select from "../../../../components/Select";
import NumberInput from "../../../../components/NumberInput";
import PasswordInput from "../../../../components/PasswordInput";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";

const POLL_INTERVAL = 10000;

const METHOD_OPTIONS = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
];

const AUTH_OPTIONS = [
  { value: "none", label: "None" },
  { value: "bearer", label: "Bearer Token" },
  { value: "basic", label: "Basic Auth" },
];

const UNIT_OPTIONS = [
  { value: "ms", label: "ms" },
  { value: "s", label: "s" },
  { value: "m", label: "m" },
  { value: "h", label: "h" },
];

const METHOD_COLORS = {
  GET: { bg: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)", border: "rgba(76,175,80,0.25)" },
  POST: { bg: "rgba(80,140,255,0.12)", color: "rgba(120,170,255,0.95)", border: "rgba(80,140,255,0.25)" },
  PUT: { bg: "rgba(255,193,7,0.12)", color: "rgba(255,193,7,0.95)", border: "rgba(255,193,7,0.25)" },
  PATCH: { bg: "rgba(255,152,0,0.12)", color: "rgba(255,152,0,0.95)", border: "rgba(255,152,0,0.25)" },
  DELETE: { bg: "rgba(244,67,54,0.12)", color: "rgba(244,67,54,0.95)", border: "rgba(244,67,54,0.25)" },
};

const MethodBadge = ({ method }) => {
  const c = METHOD_COLORS[method] || METHOD_COLORS.GET;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: "3px 8px",
      borderRadius: 5, background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontFamily: "monospace",
    }}>
      {method}
    </span>
  );
};

// ── Tags Panel ──────────────────────────────────────────────────────

const TagsPanel = ({ profileId }) => {
  const [tags, setTags] = useState(null);
  const [pathMap, setPathMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (cancelledRef) => {
    setLoading(true); setError(null);
    try {
      const result = await listProfileTags(profileId);
      const rows = result || [];
      const referencedTags = await fetchTagsByIds(rows.map((tag) => tag.id || tag.tag_id));
      const map = {};
      for (const tag of referencedTags) {
        const id = String(tag.tagId || tag.id || tag.tag_id || "");
        const path = tag.fullPath || tag.full_path || tag.path || tag.name || "";
        if (id && path) map[id] = path;
      }
      if (!cancelledRef?.current) {
        setTags(rows);
        setPathMap(map);
      }
    } catch (err) {
      if (!cancelledRef?.current) { setTags(null); setError(String(err?.message || err).trim() || "Failed to load tags"); }
    } finally { if (!cancelledRef?.current) setLoading(false); }
  }, [profileId]);

  useEffect(() => {
    const c = { current: false };
    load(c);
    return () => { c.current = true; };
  }, [load]);

  if (loading) return <span className="gwSchemaHint">Loading tags...</span>;
  if (error) return (
    <div className="gwSchemaRetryRow">
      <span className="gwSchemaError">{error}</span>
      <button className="gwSchemaRetryBtn" type="button" onClick={() => load()}>Retry</button>
    </div>
  );
  if (!tags || tags.length === 0) return <span className="gwSchemaHint">No tags using this profile</span>;

  return (
    <div className="gwSchemaTree">
      {tags.map((t) => {
        const fullPath = pathMap[String(t.id || t.tag_id)] || t.path || t.name || "Tag unavailable";
        return (
          <div key={t.id || t.tag_id} style={{ padding: "5px 8px", display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 11, color: "var(--gw-muted)", flexShrink: 0 }}>Tag</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: "var(--gw-text)" }}>{fullPath}</span>
            {t.value_path && (
              <>
                <span style={{ fontSize: 11, color: "var(--gw-muted)", flexShrink: 0 }}>Value</span>
                <span style={{ fontSize: 11, color: "var(--gw-text-secondary)", fontFamily: "monospace" }}>$.{t.value_path}</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Profile Modal ───────────────────────────────────────────────────

const ProfileModal = ({ editing, prefilled, onClose, onSaved }) => {
  const isEdit = !!editing;
  const notify = useNotify();
  const seed = editing || prefilled || null;
  const initPoll = msToHuman(seed?.poll_interval_ms || 5000);
  const initTimeout = msToHuman(seed?.timeout_ms || 10000);

  const [form, setForm] = useState({
    name: seed?.name || "",
    url: seed?.url || "",
    method: seed?.method || "GET",
    auth_mode: seed?.auth_mode || "none",
    auth_username: seed?.auth_username || "",
    auth_secret: "",
    headers: seed?.headers ? headersToJson(seed.headers) : headersToJson(editing?.headers),
  });
  const [pollValue, setPollValue] = useState(initPoll.value);
  const [pollUnit, setPollUnit] = useState(initPoll.unit);
  const [timeoutValue, setTimeoutValue] = useState(initTimeout.value);
  const [timeoutUnit, setTimeoutUnit] = useState(initTimeout.unit);
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let headers;
    if (form.headers.trim()) {
      try {
        const parsed = JSON.parse(form.headers.trim());
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          notify({ type: "error", title: "Invalid Headers", message: "Headers must be a JSON object." }); return;
        }
        headers = parsed;
      } catch {
        notify({ type: "error", title: "Invalid Headers", message: "Custom headers must be valid JSON." }); return;
      }
    }

    setSubmitting(true);
    try {
      const pollMs = convertToMs(pollValue, pollUnit);
      const timeoutMs = convertToMs(timeoutValue, timeoutUnit);

      if (isEdit) {
        const patch = {};
        if (form.name !== editing.name) patch.name = form.name;
        if (form.url !== editing.url) patch.url = form.url;
        if (form.method !== (editing.method || "GET")) patch.method = form.method;
        if (pollMs !== editing.poll_interval_ms) patch.poll_interval_ms = pollMs;
        if (timeoutMs !== editing.timeout_ms) patch.timeout_ms = timeoutMs;
        if (form.auth_mode !== (editing.auth_mode || "none") || form.auth_secret) {
          patch.auth_mode = form.auth_mode;
          if (form.auth_mode === "basic") patch.auth_username = form.auth_username;
          if (form.auth_mode !== "none") patch.auth_secret = form.auth_secret;
        }
        if (headers !== undefined) patch.headers = headers;
        else if (!form.headers.trim() && editing.headers && Object.keys(editing.headers).length > 0) patch.headers = {};
        await updateApiProfile(editing.id, patch);
      } else {
        const payload = {
          name: form.name, url: form.url, method: form.method,
          poll_interval_ms: pollMs, timeout_ms: timeoutMs, auth_mode: form.auth_mode,
        };
        if (form.auth_mode === "basic") payload.auth_username = form.auth_username;
        if (form.auth_mode !== "none") payload.auth_secret = form.auth_secret;
        if (headers) payload.headers = headers;
        await createApiProfile(payload);
      }
      notify({ type: "success", title: isEdit ? "Updated" : "Created", message: `"${form.name}" ${isEdit ? "updated" : "created"}.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: "Failed", message: String(err.message || err) });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 520 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name}` : "New API Profile"}</div>
            <div className="gwModalSubtitle">{isEdit ? "Update the profile configuration." : "Configure URL, auth, and polling for an HTTP source."}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>

        <div className="gwModalBody">
          {/* Row 1: Name + Method */}
          <div className="gwFieldRow">
            <div className="gwField" style={{ flex: 2 }}>
              <label className="gwLabel">Profile Name</label>
              <input className="gwInput" placeholder="e.g. Weather API" value={form.name} onChange={set("name")} autoFocus required />
            </div>
            <div className="gwField" style={{ flex: 1 }}>
              <label className="gwLabel">Method</label>
              <Select value={form.method} onChange={set("method")} options={METHOD_OPTIONS} />
            </div>
          </div>

          {/* Row 2: URL (full width) */}
          <div className="gwField">
            <label className="gwLabel">URL</label>
            <input className="gwInput" placeholder="https://api.example.com/data" value={form.url} onChange={set("url")} required />
          </div>

          {/* Row 3: Auth mode (full width) */}
          <div className="gwField">
            <label className="gwLabel">Authentication</label>
            <Select value={form.auth_mode} onChange={set("auth_mode")} options={AUTH_OPTIONS} />
          </div>

          {/* Row 4: Username + Password (only for basic auth) */}
          {form.auth_mode === "basic" && (
            <div className="gwFieldRow">
              <div className="gwField">
                <label className="gwLabel">Username</label>
                <input className="gwInput" placeholder="API username" value={form.auth_username} onChange={set("auth_username")} />
              </div>
              <div className="gwField">
                <label className="gwLabel">Password</label>
                <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : "Enter password"}
                  value={form.auth_secret} onChange={set("auth_secret")} />
              </div>
            </div>
          )}

          {/* Row 4 alt: Bearer token (full width) */}
          {form.auth_mode === "bearer" && (
            <div className="gwField">
              <label className="gwLabel">Bearer Token</label>
              <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : "Enter token"}
                value={form.auth_secret} onChange={set("auth_secret")} />
            </div>
          )}

          {/* Row 5: Poll + Timeout */}
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">Poll Interval</label>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <NumberInput value={pollValue} onChange={(e) => setPollValue(e.target.value)} min={0.5} step={0.5} />
                </div>
                <div style={{ width: 80 }}>
                  <Select value={pollUnit} onChange={setPollUnit} options={UNIT_OPTIONS} />
                </div>
              </div>
            </div>
            <div className="gwField">
              <label className="gwLabel">Timeout</label>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <NumberInput value={timeoutValue} onChange={(e) => setTimeoutValue(e.target.value)} min={0.5} step={0.5} />
                </div>
                <div style={{ width: 80 }}>
                  <Select value={timeoutUnit} onChange={setTimeoutUnit} options={UNIT_OPTIONS} />
                </div>
              </div>
            </div>
          </div>

          {/* Row 6: Headers */}
          <div className="gwField">
            <label className="gwLabel">Custom Headers (JSON)</label>
            <textarea className="gwTextarea" rows={3}
              placeholder={'{\n    "X-Api-Key": "your-key"\n}'}
              value={form.headers} onChange={set("headers")} />
            <div className="gwHint">Optional key-value pairs sent with every request.</div>
          </div>
        </div>

        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create Profile"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────

const ApiConnectionsPage = () => {
  const canConfigure = usePermission("configure");
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [proposed, setProposed] = useState(null);
  const [browsing, setBrowsing] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const notify = useNotify();

  const refresh = useCallback(async () => {
    try {
      setProfiles((await listApiProfiles()) || []);
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [refresh]);

  // Refresh when the AI mutates an API profile.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "api_profiles") refresh();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [refresh]);

  // AI propose-api-profile: open the New Profile modal pre-filled.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      setProposed(payload);
      setEditing(null);
      setShowCreate(true);
    };
    window.addEventListener("gw-ai-propose-api-profile-page", handler);
    return () => window.removeEventListener("gw-ai-propose-api-profile-page", handler);
  }, []);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteApiProfile(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `"${deleteTarget.name}" deleted.` });
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally { setDeleting(false); }
  };

  const hasProfiles = profiles.length > 0;

  const formatInterval = (ms) => {
    const h = msToHuman(ms);
    return `${h.value}${h.unit}`;
  };

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">API Connections</div>
            <div className="gwPageSubtitle">
              {hasProfiles ? `${profiles.length} profile${profiles.length !== 1 ? "s" : ""} configured` : "Configure reusable HTTP profiles for API tag sources."}
            </div>
          </div>
          <button className="gwPrimaryBtn" type="button" onClick={() => setShowCreate(true)}
            disabled={!canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
            <span>Add Profile</span>
          </button>
        </div>

        {loading && !hasProfiles && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading profiles...</div>
        )}

        {!loading && !hasProfiles && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No API profiles yet</div>
            <div className="gwEmptyBody">
              Create a profile to configure URL, authentication, and polling settings that can be shared across multiple tags.
            </div>
            <div className="gwEmptyActions">
              <button className="gwPrimaryBtn" type="button" onClick={() => setShowCreate(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Add Profile</span>
              </button>
            </div>
          </div>
        )}

        {hasProfiles && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Profiles</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {profiles.map((p) => (
                <div key={p.id} data-gw-resource="api_profiles" data-gw-id={p.id} data-gw-name={p.name}>
                  <div className="umUserRow" style={{ cursor: "pointer" }}
                    onClick={() => setBrowsing(browsing === p.id ? null : p.id)}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: "var(--gw-surface-2)", border: "1px solid var(--gw-border)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, color: "var(--gw-text-secondary)",
                    }}>
                      API
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="umUserName">{p.name}</span>
                        <MethodBadge method={p.method} />
                      </div>
                      <div className="umUserMeta">
                        {p.url}
                        {p.auth_mode && p.auth_mode !== "none" && ` \u00b7 ${p.auth_mode}`}
                        {` \u00b7 ${formatInterval(p.poll_interval_ms)} poll`}
                      </div>
                    </div>
                    <button type="button" className="gwIconButton" style={{ flexShrink: 0 }}
                      onClick={(e) => openMenu(e, p.id)}>
                      <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                    </button>
                    {menuOpen === p.id && (
                      <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                        <button className="gwProjectMenuItem" type="button"
                          onClick={() => { setMenuOpen(null); setBrowsing(browsing === p.id ? null : p.id); }}>
                          {browsing === p.id ? "Hide Tags" : "Show Tags"}
                        </button>
                        <button className="gwProjectMenuItem" type="button"
                          disabled={!canConfigure}
                          title={canConfigure ? undefined : "Requires configure permission"}
                          onClick={() => { setMenuOpen(null); setEditing(p); }}>
                          Edit
                        </button>
                        <div className="gwProfileDivider" />
                        <button className="gwProjectMenuItem gwProjectMenuDanger" type="button"
                          disabled={!canConfigure}
                          title={canConfigure ? undefined : "Requires configure permission"}
                          onClick={() => { setMenuOpen(null); setDeleteTarget(p); }}>
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  {browsing === p.id && (
                    <div className="gwSchemaPanel">
                      <TagsPanel profileId={p.id} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {showCreate && (
        <ProfileModal
          prefilled={proposed}
          onClose={() => { setShowCreate(false); setProposed(null); }}
          onSaved={refresh}
        />
      )}
      {editing && <ProfileModal editing={editing} onClose={() => setEditing(null)} onSaved={refresh} />}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Profile</div>
                <div className="gwModalSubtitle">This action cannot be undone.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Are you sure you want to remove <strong>{deleteTarget.name}</strong>? Tags using this profile will stop polling.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Profile"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { ApiConnectionsPage };
